<template>
  <div v-if="hasErrors" class="error-messages">
    <ul>
      <li v-for="error in errors" :key="error.code">
        {{ error.message }}
      </li>
    </ul>
  </div>

  <form class="form" @submit.prevent="handleFormSubmit">
    <label class="field">
      <div class="input">
        <input
          v-model.trim="firstname"
          type="text"
          class="input__element"
          autocomplete="given-name"
          placeholder="First name"
        />
        <div v-if="v$.firstname.$dirty" class="input__response">
          <ul>
            <li v-for="error of v$.firstname.$errors" :key="error.$uid">
              {{ error.$message }}
            </li>
          </ul>
        </div>
      </div>
    </label>

    <label class="field">
      <div class="input">
        <input
          v-model.trim="lastname"
          type="text"
          class="input__element"
          autocomplete="family-name"
          placeholder="Last name"
        />
        <div v-if="v$.lastname.$dirty" class="input__response">
          <ul>
            <li v-for="error of v$.lastname.$errors" :key="error.$uid">
              {{ error.$message }}
            </li>
          </ul>
        </div>
      </div>
    </label>

    <label class="field">
      <div class="input">
        <input
          v-model.trim="email"
          type="email"
          class="input__element"
          autocomplete="email"
          placeholder="E-mail address"
        />
        <div v-if="v$.email.$dirty" class="input__response">
          <ul>
            <li v-for="error of v$.email.$errors" :key="error.$uid">
              {{ error.$message }}
            </li>
          </ul>
        </div>
      </div>
    </label>

    <label class="field">
      <div class="input">
        <input
          v-model.trim="password"
          type="password"
          class="input__element"
          autocomplete="new-password"
          placeholder="Password"
        />
        <div v-if="v$.password.$dirty" class="input__response">
          <ul>
            <li v-for="error of v$.password.$errors" :key="error.$uid">
              {{ error.$message }}
            </li>
          </ul>
        </div>
      </div>
    </label>

    <label class="field">
      <div class="input">
        <input
          v-model.trim="confirmPassword"
          type="password"
          class="input__element"
          autocomplete="new-password"
          placeholder="Confirm Password"
        />
        <div v-if="v$.confirmPassword.$dirty" class="input__response">
          <ul>
            <li v-for="error of v$.confirmPassword.$errors" :key="error.$uid">
              {{ error.$message }}
            </li>
          </ul>
        </div>
      </div>
    </label>

    <template v-if="isPhoneNumberInputEnabled">
      <label class="field field--checkbox">
        <span class="field__label">Text me with news and offers</span>
        <div class="input input--checkbox">
          <input
            v-model="showPhoneNumberInput"
            type="checkbox"
            class="input__element input__element--checkbox"
          />
          <span class="input__checkbox"></span>
        </div>
      </label>

      <template v-if="showPhoneNumberInput">
        <div class="telephone-wrapper">
          <label class="field">
            <div class="input">
              <select
                class="input__element"
                :value="usersCountryCode"
                @change="handleCodeChange"
              >
                <option
                  v-for="code in telephoneCodesOrdered"
                  :key="code.code"
                  :value="code.code"
                >
                  {{ code.dial_code }}
                </option>
              </select>
              <div v-if="v$.selectedDialCode.$dirty" class="input__response">
                <ul>
                  <li
                    v-for="error of v$.selectedDialCode.$errors"
                    :key="error.$uid"
                  >
                    {{ error.$message }}
                  </li>
                </ul>
              </div>
            </div>
          </label>

          <label class="field">
            <div class="input">
              <input
                v-model.trim="telephone"
                type="tel"
                class="input__element"
                autocomplete="tel"
                placeholder="Phone number"
              />
              <div v-if="v$.telephone.$dirty" class="input__response">
                <ul>
                  <li v-for="error of v$.telephone.$errors" :key="error.$uid">
                    {{ error.$message }}
                  </li>
                </ul>
              </div>
            </div>
          </label>
        </div>
        <div class="legal">
          By signing up via text, you agree to receive recurring automated
          marketing messages, including cart reminders, at the phone number
          provided. Consent is not a condition of purchase. Reply STOP to
          unsubscribe. Reply HELP for help.
        </div>
      </template>
    </template>

    <div class="field">
      <Button type="submit" full-width>Create account with email</Button>
    </div>
  </form>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required, email, sameAs, numeric } from "@vuelidate/validators";
import { mapActions, mapGetters } from "vuex";
import isEmpty from "lodash-es/isEmpty";
import Button from "@/components/Button.vue";
import telephoneCodes from "@/assets/json/telephone-codes.json";
import cloneDeep from "lodash-es/cloneDeep";
import { regionFeatureToggles } from "@/config.js";

export default {
  name: "RegisterForm",
  components: {
    Button,
  },
  emits: ["success"],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      firstname: "",
      lastname: "",
      email: "",
      password: "",
      confirmPassword: "",
      telephone: "",
      codes: telephoneCodes,
      selectedDialCode: null,
      usersCountryCode: null,
      showPhoneNumberInput: false,
      errors: [],
    };
  },
  computed: {
    ...mapGetters("app", ["currencyCode"]),
    hasErrors() {
      return this.errors.length > 0;
    },
    fullTelephoneNumber() {
      return this.selectedDialCode + this.telephone?.toString();
    },
    telephoneCodesOrdered() {
      const countryCodesToMoveToTop = ["GB", "US", "AU"];
      const codes = cloneDeep(telephoneCodes);

      return codes
        .sort((a, b) => a["dial_code"] - b["dial_code"])
        .sort((a, b) => {
          return countryCodesToMoveToTop.includes(a.code)
            ? -1
            : countryCodesToMoveToTop.includes(b.code)
            ? 1
            : 0;
        });
    },
    isPhoneNumberInputEnabled() {
      return (
        regionFeatureToggles[this.currencyCode]?.phoneNumberSignUp || false
      );
    },
  },
  watch: {
    showPhoneNumberInput(val) {
      val && this.preSelectUsersTelephoneCode();
    },
  },
  methods: {
    ...mapActions("auth", ["attemptRegister"]),
    async preSelectUsersTelephoneCode() {
      const response = await fetch(
        "https://pro.ip-api.com/json?key=Y6SLxPFTt7gdeOe"
      );
      const { countryCode: usersCountryCode } = await response.json();

      const { dial_code } = this.codes.find(
        (code) => code.code === usersCountryCode
      );

      this.selectedDialCode = dial_code;
      this.usersCountryCode = usersCountryCode;
    },
    async handleSMSNotification() {
      const updateProfileResponse = await fetch(
        `${process.env.RATANDBOA_NODE_APP_URL}/klaviyo/sms-list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: this.email,
            phone_number: this.fullTelephoneNumber,
          }),
        }
      );

      if (!updateProfileResponse.ok) {
        this.success = false;
        return false;
      }

      return true;
    },
    async handleFormSubmit() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }

      if (this.showPhoneNumberInput && this.isPhoneNumberInputEnabled) {
        this.v$.telephone.$touch();
        this.v$.selectedDialCode.$touch();

        if (this.v$.telephone.$invalid || this.v$.selectedDialCode.$invalid) {
          return;
        }
      }

      let registerInput = {
        firstName: this.firstname,
        lastName: this.lastname,
        email: this.email,
        password: this.password,
      };

      if (this.showPhoneNumberInput && this.isPhoneNumberInputEnabled) {
        registerInput = {
          ...registerInput,
          phone: this.fullTelephoneNumber,
        };
      }

      const data = await this.attemptRegister(registerInput);

      if (data.customerUserErrors.length >= 1) {
        this.errors = data.customerUserErrors;
      } else {
        if (this.showPhoneNumberInput && this.isPhoneNumberInputEnabled) {
          await this.handleSMSNotification();
        }

        if (!isEmpty(data.customerAccessToken)) {
          this.$emit("success");
        }
      }
    },
  },
  validations() {
    let validations = {
      firstname: { required },
      lastname: { required },
      email: { required, email },
      password: { required },
      confirmPassword: { sameAsPassword: sameAs(this.password) },
    };

    if (this.showPhoneNumberInput) {
      validations = {
        ...validations,
        telephone: { required, numeric },
        selectedDialCode: { required },
      };
    }

    return validations;
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";

.error-messages {
  margin-top: 10px;
  margin-bottom: 10px;
  background-color: $color-red;
  padding: 16px;
  color: $color-white;
  border-radius: 2px;
  font-size: 10px;
}

.legal {
  font-size: 11px;
  margin: 0 0 30px 0;
}
</style>
